"use client";

import { motion } from "framer-motion";
import { ReactNode } from "react";
import { playButtonClick } from "@/lib/sounds";

export function PageWrapper({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="px-4 sm:px-6 py-4 w-full max-w-full"
    >
      {children}
    </motion.div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        <h1 className="text-lg font-semibold text-foreground tracking-tight">{title}</h1>
        {subtitle && <p className="text-[13px] text-muted mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

const toneBorders: Record<string, string> = {
  credit: "border-t-2 border-t-accent",
  outstanding: "border-t-2 border-t-danger",
  unmatched: "border-t-2 border-t-warning",
  aging: "border-t-2 border-t-danger",
};

const toneValueColors: Record<string, string> = {
  credit: "text-accent",
  outstanding: "text-danger",
  unmatched: "text-warning",
  aging: "text-danger",
};

export function StatCard({
  label,
  value,
  subValue,
  trend,
  delay = 0,
  tone,
  active,
  onClick,
  secondary,
  countLine,
}: {
  label: string;
  value: string;
  subValue?: string;
  trend?: "up" | "down" | "neutral";
  delay?: number;
  tone?: "credit" | "outstanding" | "unmatched" | "aging";
  active?: boolean;
  onClick?: () => void;
  secondary?: string;
  countLine?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] as const, delay }}
      className={`bg-surface rounded-lg p-4 transition-all duration-200 hover:shadow-[0_0_20px_rgba(20,184,166,0.04)] ${
        tone ? toneBorders[tone] : ""
      } ${active ? "ring-1 ring-accent/40 bg-surface-2" : ""} ${
        onClick ? "cursor-pointer" : ""
      }`}
      style={{ border: active ? undefined : "1px solid var(--border)" }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <p className="text-[11px] text-muted uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-semibold tracking-tight tabular-nums ${tone ? toneValueColors[tone] : "text-foreground"}`}>{value}</p>
      {secondary && (
        <p className="text-[12px] text-muted mt-0.5 tabular-nums">{secondary}</p>
      )}
      {subValue && (
        <p className={`text-[12px] mt-1 ${
          trend === "up" ? "text-accent" : trend === "down" ? "text-danger" : "text-muted"
        }`}>
          {subValue}
        </p>
      )}
      {countLine && (
        <p className="text-[11px] text-muted mt-1">{countLine}</p>
      )}
    </motion.div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    settled: "text-accent",
    pending: "text-warning",
    disputed: "text-danger",
    expired: "text-muted",
    verified: "text-accent",
    blacklisted: "text-danger",
    clean: "text-accent",
    reported: "text-warning",
    under_review: "text-warning",
    flagged: "text-danger",
    cancel_requested: "text-warning",
    overpaid: "text-danger",
  };

  return (
    <span className={`text-[11px] font-medium uppercase tracking-wider ${styles[status] || "text-muted"}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

export function VerificationBadge({ level }: { level: string }) {
  const config: Record<string, { label: string; className: string }> = {
    fully_verified: { label: "Verified", className: "text-accent" },
    partially_verified: { label: "Partial", className: "text-warning" },
    unverified: { label: "Unverified", className: "text-muted" },
    flagged: { label: "Flagged", className: "text-danger" },
  };
  const c = config[level] || config.unverified;
  return <span className={`text-[11px] font-medium ${c.className}`}>{c.label}</span>;
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <h3 className="text-sm font-medium text-foreground mb-1">{title}</h3>
      <p className="text-[13px] text-muted max-w-xs">{description}</p>
      {action && (
        <Button variant="ghost" size="sm" className="mt-3" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

export function SectionCard({ title, children, className = "" }: { title?: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-surface rounded-lg overflow-hidden ${className}`} style={{ border: "1px solid var(--border)" }}>
      {title && (
        <div className="px-4 py-2.5" style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}>
          <h3 className="text-[13px] font-medium text-foreground">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  onClick,
  ...props
}: {
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "danger" | "warning";
  size?: "sm" | "md";
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const variants: Record<string, string> = {
    primary: "btn-primary font-medium active:scale-[0.97]",
    secondary: "btn-secondary active:scale-[0.97]",
    ghost: "btn-ghost",
    danger: "btn-danger active:scale-[0.97]",
    warning: "bg-warning/15 text-warning border border-warning/30 hover:bg-warning/25 active:scale-[0.97]",
  };
  const sizes: Record<string, string> = {
    sm: "px-2.5 py-1.5 text-[12px] rounded-lg min-h-[36px]",
    md: "px-3.5 py-2 text-[13px] rounded-lg min-h-[44px]",
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!props.disabled) playButtonClick();
    onClick?.(e);
  };

  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 transition-all duration-[160ms] cursor-pointer ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

type TabItem = string | { label: string; shortLabel?: string; count?: number };

export function TabBar({ tabs, active, onChange }: { tabs: TabItem[]; active: string; onChange: (tab: string) => void }) {
  return (
    <div className="flex items-center gap-0 border-b border-border" role="tablist">
      {tabs.map((tab) => {
        const label = typeof tab === "string" ? tab : tab.label;
        const shortLabel = typeof tab === "string" ? undefined : tab.shortLabel;
        const count = typeof tab === "string" ? undefined : tab.count;
        return (
          <button
            key={label}
            role="tab"
            aria-selected={active === label}
            onClick={() => onChange(label)}
            className={`px-3 py-2 text-[13px] font-medium transition-colors duration-150 cursor-pointer border-b-2 -mb-px ${
              active === label
                ? "text-foreground border-accent"
                : "text-muted border-transparent hover:text-foreground"
            }`}
          >
            {shortLabel ? (
              <>
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{shortLabel}</span>
              </>
            ) : (
              label
            )}
            {count !== undefined && (
              <span className="ml-1.5 text-[10px] font-semibold tabular-nums bg-surface-3 text-muted rounded-full px-1.5 py-px">
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function ProgressBar({ percentage }: { percentage: number }) {
  return (
    <div className="h-1 bg-surface-2 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(percentage, 100)}%` }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] as const, delay: 0.2 }}
        className="h-full rounded-full bg-accent"
      />
    </div>
  );
}

export function StarRating({ score, size = "sm" }: { score: number; size?: "sm" | "md" }) {
  const full = Math.floor(score);
  const hasHalf = score - full >= 0.3;
  const textSize = size === "sm" ? "text-[11px]" : "text-sm";
  return (
    <span className={`${textSize} tabular-nums`}>
      <span className="text-warning">{"★".repeat(full)}</span>
      {hasHalf && <span className="text-warning/50">★</span>}
      <span className="text-surface-3">{"★".repeat(5 - full - (hasHalf ? 1 : 0))}</span>
      <span className="text-muted ml-1">{score.toFixed(1)}</span>
    </span>
  );
}
